// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { ImprChip, Modal, SearchTagSelect, useAutosuggest } from '@imprivata-cloud/components';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { findGroups } from '@api/portalServices';
import { type Group, SyncStatus } from '@api/types';
import { useGroups, useMarkGroup, useUnmarkGroupForSync } from '@imprivata-cloud/core';
import { transformSearchPattern } from '../userUtils';
import './GroupsModal.less';

interface GroupsModalProps {
  open: boolean;
  onClose: () => void;
  initSyncStatus?: SyncStatus;
  tenantId: string;
}

const TRANSLATION_ROOT = 'portal.settings.groups';
const extractor = (group: Group) => group.groupId;
const labelExtractor = (group: Group) => group.groupName;

const GroupsModal = ({ open, onClose, tenantId, initSyncStatus }: GroupsModalProps) => {
  const { t } = useTranslation();
  const [groupsToUnmark, setGroupsToUnmark] = useState<string[]>([]);
  const markGroup = useMarkGroup(tenantId);
  const unmarkGroupForSync = useUnmarkGroupForSync(tenantId);
  const { addedGroups } = useGroups(tenantId);

  const fetchOptions = useCallback(
    async (searchVal: string) => {
      const availableGroups = await findGroups(
        {
          selectors: {
            search: { pattern: transformSearchPattern(searchVal), fields: ['name'] },
            filters: [{ field: 'sync-enabled', value: 'false', operator: 'equals' }],
          },
        },
        tenantId,
      );
      return availableGroups.groups || [];
    },
    [tenantId],
  );

  const {
    searchValue,
    setSearchValue,
    selected,
    setSelected,
    options,
    selectedObjects: selectedGroups,
  } = useAutosuggest({ fetchOptions, extractor, labelExtractor });

  const filterSelectedOptions = useCallback(
    (inputValue: string, option: any) => {
      if (selected.includes(String(option?.value ?? ''))) { return false; }
      if (!inputValue) { return true; }
      return String(option?.label ?? '')
        .toLowerCase()
        .includes(inputValue.toLowerCase());
    },
    [selected],
  );

  const handleClose = () => {
    setGroupsToUnmark([]);
    setSelected([]);
    onClose();
  };

  const handleOk = () => {
    for (const group of selectedGroups) {
      markGroup.mutate(group.groupId);
    }
    for (const group of groupsToUnmark) {
      unmarkGroupForSync.mutate(group);
    }
    setGroupsToUnmark([]);
    setSelected([]);
    onClose();
  };

  const handleRemoveGroup = (group: string) => {
    setGroupsToUnmark([...groupsToUnmark, group]);
  };

  if (!open) {
    return null;
  }

  return (
    <Modal
      destroyOnHidden
      title={t(`${TRANSLATION_ROOT}.title`)}
      open={open}
      width={800}
      onCancel={handleClose}
      cancelText={t('common.cancel')}
      okText={t('common.save')}
      onOk={handleOk}
      maskClosable={false}
    >
      {initSyncStatus === SyncStatus.RUNNING ? (
        <p>{t(`${TRANSLATION_ROOT}.sync-in-progress`)}</p>
      ) : (
        <div className="groups-modal-content">
          <p>{t(`${TRANSLATION_ROOT}.subtitle`)}</p>

          {/* Groups already added */}
          <div className="groups-container">
            {addedGroups
              .filter(group => !groupsToUnmark.includes(group.groupId))
              .map(group => (
                <ImprChip
                  key={group.groupId}
                  label={group.groupName}
                  color="processing"
                  closable
                  onClose={() => handleRemoveGroup(group.groupId)}
                />
              ))}
          </div>

          {/* Enter new groups */}
          <p className="enter-groups-title">{t(`${TRANSLATION_ROOT}.enter-groups`)}</p>
          <SearchTagSelect
            antdProps={{
              autoFocus: true,
              virtual: false,
              // biome-ignore lint/suspicious/noExplicitAny: antd FilterFunc type uses null in DefaultOptionType
              filterOption: filterSelectedOptions,
            }}
            searchValue={searchValue}
            onSearch={setSearchValue}
            options={options}
            value={selected}
            onChange={setSelected}
            onBlur={() => {
              setSearchValue('');
            }}
          />
        </div>
      )}
    </Modal>
  );
};

export default GroupsModal;
